/**
 * /auth/login — sign-in screen. Phase 3b of ottotester#65.
 *
 * Three input paths:
 *   - Email + password (Better Auth `signIn.email`)
 *   - Google OAuth (`signIn.social` with provider 'google')
 *   - GitHub OAuth (`signIn.social` with provider 'github')
 *
 * Reads navigation state for `passwordUpdated: true` (set by
 * ConfirmPasswordReset on success) and surfaces a one-shot banner so
 * the user knows their reset took effect.
 *
 * Phase 3b is "screens exist; no route requires auth yet" (per Q2 of
 * the design discussion). On successful sign-in we redirect to '/'
 * which resolves to the active workspace via WorkspaceRedirect.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Mail } from 'lucide-react'

import { Button, Checkbox, Input, Label, PasswordInput } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'
import { OAuthRow, type OAuthProvider } from '@/auth/OAuthRow'
import { Divider } from '@/auth/Divider'
import { useIdentity } from '@/hooks/useIdentity'
import { lookupSso, type SsoLookupResult } from '@/lib/api'

interface LocationState {
  passwordUpdated?: boolean
}

/** Validate `returnTo` is a same-origin pathname; reject anything
 * that smells like an absolute URL or protocol-relative path so we
 * can't be coerced into an open redirect. */
function safeReturnTo(value: string | null): string | null {
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  return value
}

/** The "go here next" a sign-in returns when it resumed a pending
 * authorization the account service sent the customer here for (#2488).
 * Better Auth surfaces a server redirect as `{ redirect: true, url }` in the
 * response body; an ordinary sign-in returns a session and no url, so this is
 * null on every normal login. The url is the server's own resume target (the
 * client's registered redirect address), not a value from this page, so it is
 * not subject to the same-origin `returnTo` rule — the whole point is to leave
 * for the account service's callback. */
function resumeRedirectFrom(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const url = (data as { url?: unknown }).url
  return typeof url === 'string' && url.length > 0 ? url : null
}

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { refreshIdentity } = useIdentity()
  const passwordUpdated = (location.state as LocationState | null)?.passwordUpdated === true
  const returnTo = safeReturnTo(searchParams.get('returnTo'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [oauthSubmitting, setOauthSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // #1792 — does this address belong to a customer who has connected
  // their own company sign-in? Null until the first answer arrives, so
  // the block never flashes in and out while someone is still typing.
  const [sso, setSso] = useState<SsoLookupResult | null>(null)
  const [ssoSubmitting, setSsoSubmitting] = useState(false)

  // Debounced so a lookup fires when typing pauses rather than per
  // keystroke. 400ms is long enough that "dana@acme.com" costs one
  // request, short enough that the button is there before the eye
  // reaches the password field. The abort flag drops a response that
  // arrives after the address changed again, which is what stops a
  // stale answer briefly offering the wrong company.
  useEffect(() => {
    const candidate = email.trim()
    if (!candidate.includes('@') || candidate.endsWith('@')) {
      setSso(null)
      return
    }
    let abandoned = false
    const timer = window.setTimeout(() => {
      void lookupSso(candidate).then((result) => {
        if (!abandoned) setSso(result.ssoEnabled ? result : null)
      })
    }, 400)
    return () => {
      abandoned = true
      window.clearTimeout(timer)
    }
  }, [email])

  async function handleSsoSignIn() {
    setError(null)
    setSsoSubmitting(true)
    const { error: ssoError } = await authClient.signIn.sso({
      email: email.trim(),
      callbackURL: returnTo ?? '/',
    })
    if (ssoError) {
      setSsoSubmitting(false)
      setError(
        "We couldn't start sign-in with your organization. Use your email and password, or try again.",
      )
    }
    // On success the browser is being redirected to the company's own
    // page, so there is nothing to do here and the button stays busy
    // until it leaves.
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setEmailSubmitting(true)
    const { data, error } = await authClient.signIn.email({ email, password, rememberMe })
    setEmailSubmitting(false)
    if (error) {
      setError(error.message ?? 'Sign-in failed')
      return
    }
    // A pending authorization (#2488): when the account service sent this
    // customer here mid-handoff, the server resumes the authorization the
    // moment sign-in sets a session and returns where to go next. Follow it
    // with a full-page navigation, which leaves our app for that callback.
    const resumeUrl = resumeRedirectFrom(data)
    if (resumeUrl) {
      window.location.assign(resumeUrl)
      return
    }
    // Refresh the IdentityProvider's snapshot before navigating.
    // Better Auth set the session cookie on the server, but the
    // React-side identity context still holds the previous
    // (unauthenticated) state until /api/me is re-fetched. Without
    // this, the post-login navigate lands on a route whose render
    // sees status='unauthenticated' and produces a blank screen
    // until the user manually refreshes.
    await refreshIdentity()
    navigate(returnTo ?? '/', { replace: true })
  }

  async function handleOAuth(provider: OAuthProvider) {
    setError(null)
    setOauthSubmitting(true)
    const callbackURL = returnTo ?? '/'
    // `errorCallbackURL` is the documented client-side knob for
    // redirecting on OAuth failure, but it ships broken on social-
    // flow callbacks in current better-auth (issue #1580). The
    // actual redirect is wired server-side via `onAPIError.errorURL`
    // in `infrastructure/auth/betterAuth.ts`; we still pass it here
    // for forward-compatibility when the upstream bug is fixed.
    const { error } = await authClient.signIn.social({
      provider,
      callbackURL,
      errorCallbackURL: '/auth/error',
    })
    // Don't reset on success — page is about to redirect; resetting
    // would briefly re-enable the buttons mid-navigation. On error,
    // restore so the user can try a different provider.
    if (error) {
      setOauthSubmitting(false)
      setError(error.message ?? `${provider} sign-in failed`)
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your email and password, or continue with a provider."
      banner={passwordUpdated ? <PasswordUpdatedBanner /> : undefined}
      footer={
        <>
          New here?{' '}
          <Link to="/auth/signup" className="text-primary hover:underline font-medium">
            Create an account
          </Link>
        </>
      }
    >
      <OAuthRow onSelect={handleOAuth} disabled={oauthSubmitting} />

      <Divider label="or" />

      <form onSubmit={handleSubmit} className="space-y-3" aria-label="Sign in form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {sso && (
          <div className="space-y-2 rounded-md border bg-muted/40 p-3">
            <p className="text-sm">
              {sso.organizationName ? (
                <>
                  <span className="font-semibold">{sso.organizationName}</span> uses single
                  sign-on.
                </>
              ) : (
                <>Your organization uses single sign-on.</>
              )}
            </p>
            <Button
              type="button"
              className="w-full"
              onClick={handleSsoSignIn}
              disabled={ssoSubmitting || emailSubmitting || oauthSubmitting}
            >
              {ssoSubmitting ? 'Taking you there…' : 'Sign in with your organization'}
            </Button>
            <p className="text-xs text-muted-foreground">
              You&apos;ll finish signing in on your company&apos;s page.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/auth/reset" className="text-xs text-primary hover:underline">
              Forgot?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 py-1">
          <Checkbox
            id="remember"
            checked={rememberMe}
            onCheckedChange={(v) => setRememberMe(v === true)}
          />
          <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
            Keep me signed in
          </Label>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Button type="submit" disabled={emailSubmitting || oauthSubmitting} className="w-full">
          <Mail className="h-4 w-4 mr-2" />
          {emailSubmitting ? 'Signing in…' : 'Sign in with email'}
        </Button>
      </form>
    </AuthLayout>
  )
}

function PasswordUpdatedBanner() {
  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success"
    >
      <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>Password updated. Sign in with your new password.</span>
    </div>
  )
}
